import React, { useState } from "react";
import { Icon } from "@iconify/react";

/**
 * Format timestamps into readable Date & Time strings
 */
const formatDateTime = (dateValue) => {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export const LocationHistoryModal = ({
  isOpen,
  onClose,
  historyData = [],
  isLoading,
  onRestore, // Callback to trigger version restoration
}) => {
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [restoringVersion, setRestoringVersion] = useState(null);

  if (!isOpen) return null;

  const toggleExpand = (versionNumber) => {
    setExpandedVersion(expandedVersion === versionNumber ? null : versionNumber);
  };

  const handleRetrieve = async (e, versionNumber) => {
    e.stopPropagation(); // Safety check

    if (window.confirm("Are you sure you want to retrieve and restore this historical version?")) {
      try {
        setRestoringVersion(versionNumber);
        console.log("Calling onRestore handler...");
        if (onRestore) {
          await onRestore(versionNumber);
        }
      } finally {
        setRestoringVersion(null);
      }
    }
  };

  // Fields to exclude from the detail table
  const hiddenFields = ["id", "location_id", "version_number"];

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
    >
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content radius-12 border-0 shadow-lg">
          
          {/* HEADER */}
          <div className="modal-header border-bottom p-20">
            <h5 className="modal-title d-flex align-items-center gap-2 text-primary-light fw-bold">
              <Icon icon="solar:history-linear" className="text-primary-600 text-xl" /> Version Audit History
            </h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>

          {/* BODY */}
          <div className="modal-body p-20">
            {isLoading ? (
              <div className="text-center py-40">
                <Icon icon="line-md:loading-twotone-loop" className="text-primary-600 text-4xl mb-2" />
                <p className="text-secondary-light mb-0">Fetching version records...</p>
              </div>
            ) : historyData.length === 0 ? (
              <p className="text-secondary-light text-center py-40 mb-0">
                No historical records found for this location.
              </p>
            ) : (
              <div className="d-flex flex-column gap-3">
                {historyData.map((record, index) => {
                  const isLatest = index === 0;
                  const isExpanded = expandedVersion === record.version_number;

                  // Resolve Date & Time display
                  const rawDate = record.updated_at || record.created_at || record.timestamp;
                  const dateTimeLabel = formatDateTime(rawDate) || "Date Unknown";

                  // Resolve User Name display (fallback chain)
                  const userName =
                    record.updated_by_name ||
                    record.created_by_name ||
                    record.user_name ||
                    record.created_by ||
                    record.updated_by ||
                    "System Admin";

                  const isRestoringThis = restoringVersion === record.version_number;

                  return (
                    <div key={record.version_number || index} className="border radius-8 bg-neutral-50 overflow-hidden">
                      <button
                        className="w-100 p-16 border-0 bg-transparent text-start d-flex justify-content-between align-items-center cursor-pointer"
                        type="button"
                        onClick={() => toggleExpand(record.version_number)}
                      >
                        {/* LEFT SIDE: Date/Time + Badge */}
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-semibold text-primary-light text-sm">{dateTimeLabel}</span>
                          
                          {isLatest && (
                            <span className="badge bg-success-100 text-success-600 radius-4 px-8 py-4 text-xs ms-2">
                              Current Active
                            </span>
                          )}
                        </div>

                        {/* RIGHT SIDE: User Name + Collapse Icon */}
                        <div className="d-flex align-items-center gap-3">
                          <span className="text-secondary-light text-xs fw-medium">
                            {userName}
                          </span>

                          <Icon
                            icon={isExpanded ? "solar:alt-arrow-up-linear" : "solar:alt-arrow-down-linear"}
                            className="text-secondary-light"
                          />
                        </div>
                      </button>

                      {/* EXPANDED TABLE DETAILS */}
                      {isExpanded && (
                        <div className="p-16 border-top bg-white">
                          <div className="d-flex justify-content-between align-items-center mb-12">
                            <span className="text-xs text-secondary-light fw-medium">
                              Edited by <strong>{userName}</strong>
                            </span>

                            {/* RETRIEVE BUTTON (Only shown inside expanded details for older versions) */}
                            {!isLatest && (
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm radius-6 px-12 py-4 text-xs d-flex align-items-center gap-1"
                                disabled={isRestoringThis}
                                onClick={(e) => handleRetrieve(e, record.version_number)}
                              >
                                {isRestoringThis ? (
                                  <Icon icon="line-md:loading-twotone-loop" className="text-sm" />
                                ) : (
                                  <Icon icon="solar:restart-square-linear" className="text-sm" />
                                )}
                                {isRestoringThis ? "Restoring..." : "Retrieve This Version"}
                              </button>
                            )}
                          </div>

                          <div className="table-responsive">
                            <table className="table table-sm table-bordered mb-0">
                              <thead className="bg-neutral-100">
                                <tr>
                                  <th style={{ width: "35%" }} className="text-xs text-secondary-light">Field</th>
                                  <th className="text-xs text-secondary-light">Historical Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(record)
                                  .filter(([key]) => !hiddenFields.includes(key))
                                  .map(([key, value]) => (
                                    <tr key={key}>
                                      <td className="fw-semibold text-secondary-light text-xs">{key}</td>
                                      <td className="text-xs">
                                        {value === null || value === "" ? (
                                          <span className="text-neutral-400 fst-italic">Empty</span>
                                        ) : (
                                          String(value)
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div className="modal-footer border-top p-16">
            <button type="button" className="btn btn-secondary px-24 radius-8 text-sm" onClick={onClose}>
              Close
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LocationHistoryModal;